import { useRef, type ReactNode } from "react";
import { m as motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";

interface Props {
  children: ReactNode;
  className?: string;
  /** Pointer magnetism strength (0 = none). Set to 0 to keep tap feedback only. */
  strength?: number;
  /** Active/tap scale — shared across mouse and touch. */
  tapScale?: number;
}

/**
 * Wraps any CTA to give it a unified interactive feel:
 *  - Desktop: subtle magnetic pull toward the cursor.
 *  - Touch / keyboard: a snappy `whileTap` scale so the same tactile
 *    response is present on mobile (where there is no hover).
 *  - Honors `prefers-reduced-motion` for both behaviors.
 */
export function Magnetic({ children, className, strength = 0.25, tapScale = 0.96 }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 18, mass: 0.4 });
  const reduce = useReducedMotion();

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * strength);
    y.set((e.clientY - r.top - r.height / 2) * strength);
  };

  const reset = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      onPointerUp={reset}
      style={{ x: sx, y: sy }}
      whileTap={reduce ? undefined : { scale: tapScale }}
      transition={{ type: "spring", stiffness: 400, damping: 24 }}
      className={`inline-block touch-manipulation ${className ?? ""}`}
    >
      {children}
    </motion.div>
  );
}
